import React, { useContext } from "react";
import {
    ConfirmDialog,
    createFailure,
    createListTableColumns,
    createValidationResult,
    FailureSeverity,
    FormValidationSummary,
    ShimmeredDetailsList,
    useNotifications,
    usePerformMultipleActions,
    ValidationFailure,
    renderOptionalTextWithTooltip,
    renderTextWithTooltip
} from "sprint-common-web";

import { useFileRecordApi } from "../../../../../common/api/master-api/file-records/useFileRecordApi";
import { renderActionStatus } from "../../../../../common/utils/renderActionStatus";
import { isFileRecordRemovable } from "../../../remove/shouldRemoveButtonBeDisabled";
import { FileRecordListingContext } from "../../context/FileRecordListingContext";
import { RemoveFileRecordItem, ProcessRemoveFileRecordItem } from "./RemoveFileRecordItem";

interface RemoveFileRecordDialogProps {
    isOpen: boolean;
    onClose(): void;
    selectedItems: RemoveFileRecordItem[];
}

export const RemoveFileRecordDialog: React.VFC<RemoveFileRecordDialogProps> = ({ isOpen, onClose, selectedItems }) => {
    const { removeFileRecord } = useFileRecordApi();

    const { removeFileRecord: removeRecordInContext } = useContext(FileRecordListingContext);

    const { success, error } = useNotifications();

    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<RemoveFileRecordItem>({
        isAvailable: (item) => {
            return isFileRecordRemovable({
                status: item.originalStatus,
                isNotificationApproved: item.isNotificationApproved
            });
        },
        selectedItems
    });

    const failure = validateDiscardedItems(items, selectedItems.length);

    const validationResult = createValidationResult([failure]);

    const handleApprove = async () => {
        const results = await approveAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            handleClose();

            success("Removed file records successfully");
        } else error("Failed to remove file records");
    };

    const approveAllItems = async () => {
        return performAllItems(async (item) => {
            await removeFileRecord({ fileRecordId: item.id });

            removeRecordInContext(item.id);
        });
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <ConfirmDialog
            title="Remove file records"
            text="Are you sure you want to remove given file records?"
            hidden={!isOpen}
            isBlocking={isActionInProgress}
            width={"65%"}
            contentHeight={500}
            confirm={{ onClick: handleApprove, disabled: !items.length }}
            cancel={{ onClick: handleClose }}
        >
            <FormValidationSummary validationResult={validationResult} />
            {!!items.length && <ApproveNotificationsSummary items={items} />}
        </ConfirmDialog>
    );
};

interface ApproveNotificationsSummaryProps {
    items: RemoveFileRecordItem[];
}

const ApproveNotificationsSummary: React.VFC<ApproveNotificationsSummaryProps> = ({
    items
}: ApproveNotificationsSummaryProps) => {
    const columns = createListTableColumns<ProcessRemoveFileRecordItem>([
        {
            type: "basic",
            key: "deliveryFileName",
            name: "Delivery file name",
            minWidth: 80,
            maxWidth: 250,
            onRender: renderTextWithTooltip
        },
        {
            type: "basic",
            key: "filePath",
            name: "File path",
            minWidth: 60,
            maxWidth: 300,
            onRender: renderOptionalTextWithTooltip
        },
        {
            type: "basic",
            key: "content",
            name: "Content",
            minWidth: 60,
            maxWidth: 300
        },
        {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 60
        },
        {
            type: "basic",
            key: "processStatus",
            name: "Process status",
            minWidth: 100,
            onRender: renderActionStatus
        }
    ]);

    return <ShimmeredDetailsList columns={columns} items={items} layoutMode="justified" />;
};

const validateDiscardedItems = (
    items: RemoveFileRecordItem[],
    selectedItemsCount: number
): ValidationFailure | void => {
    const discardedOrdersInfoMessage = `File records with approved buzz notifications and those in "Qc Done" or "Destroyed" status have been discarded`;

    if (!items.length) {
        return createFailure(
            `${discardedOrdersInfoMessage}. Nothing to remove.`,
            "no-orders-to-remove",
            FailureSeverity.Error
        );
    } else if (items.length < selectedItemsCount) {
        return createFailure(discardedOrdersInfoMessage, "file-records-discarded-from-remove", FailureSeverity.Warning);
    }
};
